import { memo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { useAtom } from 'jotai';
import { LogOut, ShieldUser } from 'lucide-react';
import { isNonNullish } from 'remeda';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';

import LogoMini from '@assets/LogoMini.svg?react';

import { BurgerMenu } from './burgerMenu/BurgerMenu';
import { GeoSelect } from './geoSelect/GeoSelect';
import { LocaleSelect } from './localeSelect/LocaleSelect';

import css from './Header.module.css';

import { authService } from 'v2/api/authService';
import { geoAtom } from 'v2/atoms/geoAtom';
import { userAtom } from 'v2/atoms/userAtom';
import { EnvConfig } from 'v2/config';
import { DESKTOP_VIEW, Geo, userRoleToUIMap } from 'v2/constants';
import { AppRoute } from 'v2/routes';
import { Nullable } from 'v2/types';

export const Header = memo(() => {
  const navigate = useNavigate();

  const isDesktop = useMediaQuery(`(min-width: ${DESKTOP_VIEW}px)`);

  const [user, setUser] = useAtom(userAtom);
  const [geo] = useAtom(geoAtom);

  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const { trigger } = useSWRMutation('logout', authService.logout, {
    onSuccess: () => {
      setUser(null);
      navigate(AppRoute.LOGIN_PAGE, { replace: true });
      handleClose();
    },
  });

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    trigger();
  };

  return (
    isNonNullish(user) && (
      <header className={css.header}>
        {!isDesktop && <BurgerMenu />}

        <div className={css.headerContent}>
          <NavLink className={css.headerLogo} to={AppRoute.ROOT}>
            <LogoMini />
          </NavLink>

          {isDesktop && (
            <div className={css.headerTitleContainer}>
              <p className={css.headerTitle}>ADMIN PANEL</p>

              {EnvConfig.APP_ENV !== 'prod' && <span className={css.headerTitleMark}>[{EnvConfig.APP_ENV}]</span>}
              {geo === Geo.KZ && <span className={css.headerTitleMark}>[KZ]</span>}
            </div>
          )}
        </div>

        <div className={css.headerActions}>
          <GeoSelect />
          <LocaleSelect />

          <Button
            className={css.headerUserMenuTrigger}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            variant="text"
            endIcon={<ShieldUser />}
          >
            {user.name}
          </Button>
        </div>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <div className={css.headerUserRoles}>
            {user.roles.map((r) => (
              <Chip key={r} label={userRoleToUIMap[r]} size="small" />
            ))}
          </div>

          <Divider />

          <MenuItem className={css.headerUserMenuItem} onClick={handleLogout}>
            <LogOut width={16} height={18} /> Log Out
          </MenuItem>
        </Menu>
      </header>
    )
  );
});

Header.displayName = 'Header';
